import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { createAliceAgentApp, listAliceAgentApps } from 'client/api/services/aliceAgentApps';
import { IndexLayout } from 'client/blocks/Layout/Layout';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { routes } from 'client/lib/routes';
import { AliceAgentAppListItem } from 'client/model/aliceAgentApp';
import { Channel } from 'client/model/skill';
import { canCreateAliceExternalApp, canCreateAliceInternalAgentApp, canManageAliceAgentAppAccess } from 'client/utils';
import * as keyset from 'i18n/AliceAgentAppForm';

import { PlusIcon } from '../AddNewAppCard/AddNewAppIcons';
import { SkillIcon } from '../AddNewAppCard/AddNewSkillIcon';
import { AppsIndexNavbar } from '../AppsIndex/AppsIndexNavbar';

import { AliceAgentAppAccessManager } from './AliceAgentAppAccessManager';
import { AliceAgentAppCard } from './AliceAgentAppCard';
import styles from './AliceAppsIndexPage.module.scss';

const t = i18n(keyset);

interface CreateCardProps {
    title: string;
    onClick?: () => void;
    disabled?: boolean;
}

const CreateCard: React.FC<CreateCardProps> = ({ title, onClick, disabled }) => (
    <div className={clx(styles.createCardBorderWrapper, disabled && styles.createCardBorderWrapper_disabled)}>
        <button
            type="button"
            className={clx(styles.createCardContainer, disabled && styles.createCardContainer_disabled)}
            onClick={onClick}
            disabled={disabled}
        >
            <div className={styles.createCardContent}>
                <PlusIcon />
                <h4 className={styles.createCardTitle}>{title}</h4>
            </div>
            <div className={styles.createCardIconContainer}>
                <div className={styles.createCardIconWrapper}>
                    <SkillIcon channel={Channel.AliceSkill} />
                </div>
            </div>
        </button>
    </div>
);

export const AliceAppsIndexPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { add: toasterAdd } = useToaster();
    const [isCreating, setIsCreating] = useState(false);
    const [items, setItems] = useState<AliceAgentAppListItem[] | undefined>();
    const showAll = location.pathname === routes.app.aliceAppsAll;

    const fetchList = useCallback(async () => {
        try {
            setItems(await listAliceAgentApps(showAll));
        } catch {
            toasterAdd({ title: t('list-load-error'), theme: 'danger', name: 'LIST_ALICE_AGENT_APPS' });
            setItems([]);
        }
    }, [toasterAdd, showAll]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleCreateInternalAgent = async () => {
        if (isCreating) return;
        setIsCreating(true);
        try {
            const { skillId } = await createAliceAgentApp({
                name: t('new-alice-agent'),
                type: 'aliceInternalAgentApp',
            });
            navigate(routes.app.aliceAgentApp(skillId));
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('create-error');
            toasterAdd({ title: message, theme: 'danger', name: 'CREATE_ALICE_AGENT_APP' });
        } finally {
            // The success path unmounts via navigate(), but a navigation that's silently cancelled
            // (e.g. route guard) would otherwise leave the button stuck disabled. `finally` keeps
            // the lock release symmetric with the catch branch.
            setIsCreating(false);
        }
    };

    const handleCreateExternalApp = async () => {
        if (isCreating) return;
        setIsCreating(true);
        try {
            const { skillId } = await createAliceAgentApp({
                name: t('new-alice-agent'),
                type: 'aliceExternalApp',
            });
            navigate(routes.app.aliceExternalApp(skillId));
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('create-error');
            toasterAdd({ title: message, theme: 'danger', name: 'CREATE_ALICE_AGENT_APP' });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <IndexLayout>
            <article className={styles.pageContainer}>
                <AppsIndexNavbar channel={Channel.AliceSkill} />
                {canManageAliceAgentAppAccess() && <AliceAgentAppAccessManager />}
                <div className={styles.cardListContainer}>
                    <div className={styles.cardList}>
                        {canCreateAliceInternalAgentApp() && (
                            <CreateCard
                                title={t('create-alice-agent')}
                                onClick={handleCreateInternalAgent}
                                disabled={isCreating}
                            />
                        )}
                        {canCreateAliceExternalApp() && (
                            <CreateCard
                                title={t('create-alice-app')}
                                onClick={handleCreateExternalApp}
                                disabled={isCreating}
                            />
                        )}
                        {items?.map((item) => (
                            <AliceAgentAppCard key={item.skillId} item={item} />
                        ))}
                    </div>
                    {items === undefined && <PageLoader />}
                </div>
            </article>
        </IndexLayout>
    );
};
